// Sprint 6 (Phase 1) — Settings store.
//
// Holds the global "Show developer details" toggle (D47) and (post Phase 10)
// notification + sound preferences. Hydrates from `GET /api/settings` on
// app mount and PATCHes back (debounced) on every mutation.
//
// Default: developerMode = false. The founder-first vision dominates: the
// only people who flip this on are debugging something specific. The header
// chip + per-message ▽ stay visible escape hatches.

import { create } from 'zustand';
import { api } from '../lib/api';
import type { Locale } from '../i18n';
import type { PermissionMode } from '../types';

// Sprint notification-center (spec D3) — one matrix, not N toggles.
// Only the configurable cells persist; the fixed tier policy lives in
// the backend NotificationRouter.
export interface NotificationTiers {
  actionRequiredPush: boolean;
  informationalFeed: boolean;
}

export interface SettingsState {
  developerMode: boolean;
  // Phase 10 — wired to the Settings page UI. Sprint notification-center
  // removed `telegramEvents` (dead toggle, spec D3); `soundOnFinished`
  // is now the live notification-sound preference.
  telegramEnabled: boolean;
  soundOnFinished: boolean;
  notificationTiers: NotificationTiers;
  // Sprint UI-5 — display language (Layer 7 D68).
  language: Locale;
  // Auto-permission-mode sprint — default permission mode for new agents.
  defaultPermissionMode: PermissionMode;
  // Internal — guards the hydrate-once contract.
  hydrated: boolean;

  setDeveloperMode: (v: boolean) => void;
  setTelegramEnabled: (v: boolean) => void;
  setSoundOnFinished: (v: boolean) => void;
  setNotificationTiers: (v: NotificationTiers) => void;
  setLanguage: (lang: Locale) => void;
  setDefaultPermissionMode: (mode: PermissionMode) => void;
  hydrate: () => Promise<void>;
  // Test seam — lets unit tests reset state between cases without a network
  // round-trip.
  resetForTests: () => void;
}

const DEFAULTS: Pick<
  SettingsState,
  'developerMode' | 'telegramEnabled' | 'soundOnFinished' | 'notificationTiers' | 'language' | 'defaultPermissionMode' | 'hydrated'
> = {
  developerMode: false,
  telegramEnabled: true,
  // Default ON (was false while dead): the founder asked for "un
  // sonidito"; attention events are audible unless he turns them off.
  soundOnFinished: true,
  notificationTiers: { actionRequiredPush: true, informationalFeed: true },
  // Pau's deployment is single-user and Spanish (the whole chrome + these
  // very comments are in Spanish). 'en' was a leftover from Santi's fork
  // and made /office-live render English labels before/without a stored
  // preference. Default to 'es'; the Settings selector (now persisted via
  // the backend allow-list) lets it switch to 'en' if ever wanted.
  language: 'es' as Locale,
  defaultPermissionMode: 'agent',
  hydrated: false,
};

interface SettingsResponse {
  settings: {
    developerMode?: boolean;
    telegramEnabled?: boolean;
    soundOnFinished?: boolean;
    notificationTiers?: Partial<NotificationTiers>;
    language?: string;
    defaultPermissionMode?: string;
  };
}

let pendingPatch: ReturnType<typeof setTimeout> | null = null;
const PATCH_DEBOUNCE_MS = 500;

function schedulePatch(payload: Record<string, unknown>): void {
  if (pendingPatch !== null) clearTimeout(pendingPatch);
  pendingPatch = setTimeout(() => {
    pendingPatch = null;
    void api('/api/settings', { method: 'PATCH', body: payload }).catch(() => {
      // Settings PATCH failures are non-fatal — the optimistic store update
      // already happened, the network just couldn't persist it. The next
      // hydrate (page reload) will reconcile.
    });
  }, PATCH_DEBOUNCE_MS);
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  ...DEFAULTS,

  setDeveloperMode: (v) => {
    set({ developerMode: v });
    if (get().hydrated) schedulePatch({ developerMode: v });
  },

  setTelegramEnabled: (v) => {
    set({ telegramEnabled: v });
    if (get().hydrated) schedulePatch({ telegramEnabled: v });
  },

  setSoundOnFinished: (v) => {
    set({ soundOnFinished: v });
    if (get().hydrated) schedulePatch({ soundOnFinished: v });
  },

  setNotificationTiers: (v) => {
    set({ notificationTiers: v });
    if (get().hydrated) schedulePatch({ notificationTiers: v });
  },

  setLanguage: (lang) => {
    set({ language: lang });
    if (get().hydrated) schedulePatch({ language: lang });
  },

  setDefaultPermissionMode: (mode) => {
    set({ defaultPermissionMode: mode });
    if (get().hydrated) schedulePatch({ defaultPermissionMode: mode });
  },

  hydrate: async () => {
    if (get().hydrated) return;
    try {
      const r = await api<SettingsResponse>('/api/settings');
      const s = r.settings ?? {};
      const lang = s.language === 'en' || s.language === 'es' ? s.language as Locale : DEFAULTS.language;
      // Sprint mode-trust-mode-truth-up — legacy 'auto' coerces to 'agent'.
      // The two modes are runtime-aliases (CLI flag bypassPermissions + PreToolUse
      // hook) and the picker no longer exposes 'auto'; coercing on hydrate keeps
      // the Settings page radio aligned with the picker.
      const dpm =
        s.defaultPermissionMode === 'agent' || s.defaultPermissionMode === 'plan'
          ? (s.defaultPermissionMode as PermissionMode)
          : s.defaultPermissionMode === 'auto'
            ? ('agent' as PermissionMode)
            : DEFAULTS.defaultPermissionMode;
      set({
        developerMode: typeof s.developerMode === 'boolean' ? s.developerMode : DEFAULTS.developerMode,
        telegramEnabled:
          typeof s.telegramEnabled === 'boolean' ? s.telegramEnabled : DEFAULTS.telegramEnabled,
        soundOnFinished:
          typeof s.soundOnFinished === 'boolean' ? s.soundOnFinished : DEFAULTS.soundOnFinished,
        notificationTiers: {
          actionRequiredPush:
            typeof s.notificationTiers?.actionRequiredPush === 'boolean'
              ? s.notificationTiers.actionRequiredPush
              : DEFAULTS.notificationTiers.actionRequiredPush,
          informationalFeed:
            typeof s.notificationTiers?.informationalFeed === 'boolean'
              ? s.notificationTiers.informationalFeed
              : DEFAULTS.notificationTiers.informationalFeed,
        },
        language: lang,
        defaultPermissionMode: dpm,
        hydrated: true,
      });
    } catch {
      // Settings endpoint missing or 4xx — keep defaults, mark hydrated so
      // we don't loop on retry. Subsequent mutations still PATCH.
      set({ hydrated: true });
    }
  },

  resetForTests: () => set({ ...DEFAULTS }),
}));
